import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { fuseAnimations } from '../../../../../../../@fuse/animations';
import { FuseDirectivesModule } from '../../../../../../../@fuse/directives/directives';
import { FuseMatSidenavHelperService } from '../../../../../../../@fuse/directives/fuse-mat-sidenav/fuse-mat-sidenav.service';
import { FuseMatchMediaService } from '../../../../../../../@fuse/services/match-media.service';
import { ChatApiService } from '../../../../../../gt2/api/chat-api.service';
import { ChatStore } from '../../../../../../services/chat/chat-store.service';
import { escapeHtml } from '../../../chat-body.util';
import { ChatConversation, ChatMessage } from '../../../models/chat.models';
import { locale as english } from '../../../i18n/en-CA';
import { locale as french } from '../../../i18n/fr-CA';
import {
  NewConversationDialogComponent,
  NewConversationResult,
} from '../new-conversation-dialog/new-conversation-dialog.component';

/** Liste des conversations — branchée sur le ChatStore (signals). */
@Component({
  selector: 'chat-chats-sidenav',
  templateUrl: './chats.component.html',
  styleUrls: ['./chats.component.scss'],
  standalone: true,
  imports: [
    MatToolbarModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    FormsModule,
    MatButtonModule,
    CommonModule,
    TranslateModule,
    FuseDirectivesModule,
  ],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations,
})
export class ChatChatsSidenavComponent {
  public readonly searchText = signal('');

  public readonly filteredConversations = computed(() => {
    const term = this.searchText().toLowerCase().trim();
    const conversations = this.store.sortedConversations();
    if (term === '') {
      return conversations;
    }
    return conversations.filter((c) => (c.display_name ?? c.name ?? '').toLowerCase().includes(term));
  });

  // ---- Recherche full-text serveur (T7) : ≥ 3 chars, debounce 300 ms ----
  public readonly searchResults = signal<{ conversation: ChatMessage['conversation']; messages: ChatMessage[] }[] | null>(null);
  public readonly searching = signal(false);
  public readonly searchInActiveOnly = signal(false);
  private searchDebounce: ReturnType<typeof setTimeout> | null = null;
  private static readonly SEARCH_MIN_CHARS = 3; // aligné sur innodb_ft_min_token_size
  private static readonly SEARCH_DEBOUNCE_MS = 300;

  private readonly breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];

  constructor(
    public store: ChatStore,
    private chatApi: ChatApiService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private _fuseMatSidenavHelperService: FuseMatSidenavHelperService,
    private _fuseMatchMediaService: FuseMatchMediaService,
  ) {
    this.translate.setTranslation(english.lang, english.data, true);
    this.translate.setTranslation(french.lang, french.data, true);
  }

  public onSearchChanged(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }

    const term = this.searchText().trim();
    if (term.length < ChatChatsSidenavComponent.SEARCH_MIN_CHARS) {
      this.searchResults.set(null);
      this.searching.set(false);
      return;
    }

    this.searching.set(true);
    this.searchDebounce = setTimeout(() => this.runSearch(term), ChatChatsSidenavComponent.SEARCH_DEBOUNCE_MS);
  }

  public toggleSearchScope(): void {
    this.searchInActiveOnly.update((v) => !v);
    this.onSearchChanged();
  }

  private runSearch(term: string): void {
    const conversationUuid = this.searchInActiveOnly() ? this.store.activeConversationId() : null;
    this.chatApi.searchMessages(term, conversationUuid).subscribe({
      next: (messages) => {
        if (this.searchText().trim() !== term) {
          return; // réponse périmée (le texte a changé pendant l'appel)
        }
        // Groupement par conversation, ordre d'apparition (déjà trié pertinence).
        const groups = new Map<string, { conversation: ChatMessage['conversation']; messages: ChatMessage[] }>();
        for (const message of messages) {
          const key = message.conversation?.uuid ?? message.conversation_uuid;
          if (!groups.has(key)) {
            groups.set(key, { conversation: message.conversation ?? null, messages: [] });
          }
          groups.get(key)!.messages.push(message);
        }
        this.searchResults.set([...groups.values()]);
        this.searching.set(false);
      },
      error: () => {
        this.searchResults.set([]);
        this.searching.set(false);
      },
    });
  }

  /** Extrait avec highlight <mark> des termes — sur texte ÉCHAPPÉ (XSS-safe). */
  public highlightExcerpt(message: ChatMessage): string {
    const body = message.body ?? '';
    const escaped = escapeHtml(body.length > 160 ? `${body.slice(0, 160)}…` : body);
    const terms = this.searchText()
      .trim()
      .split(/\s+/)
      .map((t) => t.replace(/\*+$/, ''))
      .filter((t) => t.length >= 2)
      .map((t) => escapeHtml(t).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    if (terms.length === 0) {
      return escaped;
    }
    return escaped.replace(new RegExp(`(${terms.join('|')})`, 'gi'), '<mark>$1</mark>');
  }

  public openSearchResult(group: { conversation: ChatMessage['conversation']; messages: ChatMessage[] }, message: ChatMessage): void {
    const uuid = group.conversation?.uuid ?? message.conversation_uuid;
    this.store.openConversation(uuid);
    // scroll-to seulement si déjà chargé (jump contextuel = V1.5).
    setTimeout(() => document.getElementById(`chat-msg-${message.uuid}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    this.clearSearch();
  }

  public clearSearch(): void {
    this.searchText.set('');
    this.searchResults.set(null);
    this.searching.set(false);
  }

  public openConversation(conversation: ChatConversation): void {
    this.store.openConversation(conversation.uuid);

    const currentBreakpoint = this._fuseMatchMediaService.activeMediaQuery;
    const isLargeScreen = this.breakpoints.indexOf(currentBreakpoint) > this.breakpoints.indexOf('md');
    if (!isLargeScreen) {
      this._fuseMatSidenavHelperService.getSidenav('chat-left-sidenav').toggle();
    }
  }

  public newConversation(): void {
    this.dialog
      .open<NewConversationDialogComponent, void, NewConversationResult>(NewConversationDialogComponent, {
        // Right side panel pleine hauteur (mobile : le bouton Créer du popup
        // était inatteignable) — footer bar fixe géré par le composant.
        panelClass: 'chat-nc-panel',
        width: '420px',
        maxWidth: '100vw',
        height: '100vh',
        position: { top: '0', right: '0' },
        autoFocus: '[cdkFocusInitial]',
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }
        if (result.type === 'direct') {
          this.store.createDirect(result.userUuid);
        } else {
          this.store.createGroup(result.name, result.userUuids);
        }
      });
  }

  public displayName(conversation: ChatConversation): string {
    return conversation.display_name ?? conversation.name ?? '';
  }

  public avatar(conversation: ChatConversation): string | null {
    return conversation.display_avatar ?? conversation.avatar_url ?? null;
  }

  public initials(conversation: ChatConversation): string {
    const name = this.displayName(conversation);
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  /** Extrait du dernier message selon son type (deleted / attachment / system / texte). */
  public excerpt(conversation: ChatConversation): string {
    const last = conversation.last_message;
    if (!last) {
      return '';
    }
    if (last.deleted) {
      return this.translate.instant('CHAT.DELETED_MESSAGE');
    }
    if (last.type === 'image' || last.type === 'file') {
      const caption = last.body ? ` ${last.body}` : '';
      return `📎 ${this.translate.instant('CHAT.ATTACHMENT')}${caption}`;
    }
    if (last.type === 'system') {
      const action = last.metadata?.action ?? '';
      return this.translate.instant(`CHAT.SYSTEM.${action}`);
    }
    return last.body ?? '';
  }
}
